// src/pages/AdminEmailSequences.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import {
  FaBolt,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEnvelopeOpenText,
  FaExclamationTriangle,
  FaEye,
  FaPaperPlane,
  FaPauseCircle,
  FaRedo,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';

type EmailSequenceJob = {
  id: string;
  recipient_email: string;
  recipient_name?: string | null;
  trigger_key?: string | null;
  template_key: string;
  subject?: string | null;
  status: 'pending' | 'sent' | 'failed' | 'cancelled' | string;
  scheduled_for?: string | null;
  sent_at?: string | null;
  attempts?: number | null;
  last_error?: string | null;
  payload?: Record<string, any> | null;
  metadata?: Record<string, any> | null;
  created_at?: string | null;
};

type Stats = {
  pending: number;
  sent: number;
  failed: number;
  cancelled: number;
  total: number;
};

const TEMPLATE_OPTIONS = [
  {
    key: 'purchase_thank_you',
    label: 'Merci après achat',
    trigger: 'purchase_completed',
    description: 'Remercie le client, rappelle où retrouver ses achats et propose les prochaines étapes.',
  },
  {
    key: 'pre_experience_reminder',
    label: 'Rappel avant expérience',
    trigger: 'reservation_upcoming',
    description: 'Rappel pratique avant une réservation ou une expérience planifiée.',
  },
  {
    key: 'post_experience_review_request',
    label: 'Demande d’avis après expérience',
    trigger: 'experience_completed',
    description: 'Invite le client à laisser un avis après utilisation ou réservation complétée.',
  },
  {
    key: 'gift_card_cross_sell',
    label: 'Suggestion carte cadeau Forfeo',
    trigger: 'cross_sell_gift_card',
    description: 'Propose la Carte Accès Privilège Forfeo comme prochain cadeau local.',
  },
  {
    key: 'local_recommendations',
    label: 'Suggestions locales personnalisées',
    trigger: 'recommendations_followup',
    description: 'Relance douce avec Last Call, régions, calendrier des occasions et idées cadeaux.',
  },
];

const STATUS_OPTIONS = [
  { key: 'all', label: 'Toutes' },
  { key: 'pending', label: 'En attente' },
  { key: 'sent', label: 'Envoyées' },
  { key: 'failed', label: 'Échecs' },
  { key: 'cancelled', label: 'Annulées' },
];

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function statusBadge(status: string) {
  if (status === 'sent') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  if (status === 'failed') return 'bg-red-50 text-red-700 border-red-100';
  if (status === 'cancelled') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-amber-50 text-amber-700 border-amber-100';
}

function statusIcon(status: string) {
  if (status === 'sent') return <FaCheckCircle />;
  if (status === 'failed') return <FaExclamationTriangle />;
  if (status === 'cancelled') return <FaPauseCircle />;
  return <FaClock />;
}

export default function AdminEmailSequences() {
  const [jobs, setJobs] = useState<EmailSequenceJob[]>([]);
  const [stats, setStats] = useState<Stats>({ pending: 0, sent: 0, failed: 0, cancelled: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const [form, setForm] = useState({
    recipient_email: '',
    recipient_name: '',
    template_key: 'post_experience_review_request',
    scheduled_for: '',
    experience_title: 'Votre expérience Forfeo',
    region: 'Québec',
    business_name: '',
    voucher_code: '',
    review_url: 'https://forfeoexplore.com/avis-experience',
    notes: '',
  });

  const selectedTemplate = useMemo(
    () => TEMPLATE_OPTIONS.find((item) => item.key === form.template_key) || TEMPLATE_OPTIONS[0],
    [form.template_key]
  );

  const filteredJobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((job) => {
      const haystack = [
        job.recipient_email,
        job.recipient_name,
        job.template_key,
        job.trigger_key,
        job.subject,
        job.payload?.experience_title,
        job.payload?.business_name,
        job.payload?.voucher_code,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [jobs, search]);

  const callFunction = async (body: Record<string, any>) => {
    const { data, error } = await supabase.functions.invoke('email-sequences', { body });
    if (error) throw error;
    if (data && data.ok === false) throw new Error(data.error || data.message || 'Erreur Edge Function');
    return data;
  };

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const data = await callFunction({ action: 'list', status, limit: 80 });
      setJobs(Array.isArray(data?.jobs) ? data.jobs : []);
      setStats(data?.stats || { pending: 0, sent: 0, failed: 0, cancelled: 0, total: 0 });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Impossible de charger les séquences.' });
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const buildPayload = () => ({
    experience_title: form.experience_title,
    region: form.region,
    business_name: form.business_name,
    voucher_code: form.voucher_code,
    review_url: form.review_url || 'https://forfeoexplore.com/avis-experience',
    notes: form.notes,
  });

  const handlePreview = async () => {
    setActionLoading('preview');
    setMessage(null);
    try {
      const data = await callFunction({
        action: 'preview',
        template_key: form.template_key,
        recipient_email: form.recipient_email || 'client@example.com',
        recipient_name: form.recipient_name || 'Client Forfeo',
        payload: buildPayload(),
      });
      setPreviewHtml(data?.html || '');
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Prévisualisation impossible.' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreate = async (sendNow = false) => {
    if (!form.recipient_email.trim()) {
      setMessage({ type: 'error', text: 'Entre un courriel client.' });
      return;
    }

    setActionLoading(sendNow ? 'send-now' : 'create');
    setMessage(null);
    try {
      const data = await callFunction({
        action: sendNow ? 'send_test' : 'enqueue',
        recipient_email: form.recipient_email,
        recipient_name: form.recipient_name,
        template_key: form.template_key,
        trigger_key: selectedTemplate.trigger,
        scheduled_for: form.scheduled_for || null,
        payload: buildPayload(),
        metadata: { created_from: 'admin_email_sequences' },
      });
      setMessage({
        type: 'success',
        text: sendNow ? 'Courriel envoyé.' : `Séquence créée${data?.job?.id ? ` (${data.job.id.slice(0, 8)})` : ''}.`,
      });
      await loadJobs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Action impossible.' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleJobAction = async (jobId: string, action: 'send_now' | 'cancel' | 'retry') => {
    setActionLoading(`${action}:${jobId}`);
    setMessage(null);
    try {
      await callFunction({ action, job_id: jobId });
      setMessage({ type: 'success', text: action === 'cancel' ? 'Séquence annulée.' : 'Traitement lancé.' });
      await loadJobs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Action impossible.' });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="animate-in fade-in space-y-8">
      <div className="rounded-[2rem] bg-slate-950 text-white p-6 md:p-8 shadow-xl overflow-hidden relative">
        <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute -left-12 bottom-0 h-36 w-36 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-[0.25em] text-cyan-200 mb-4">
              <FaEnvelopeOpenText /> Automatisation client
            </div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">Séquences emails Forfeo</h2>
            <p className="mt-3 text-slate-300 max-w-2xl font-medium leading-relaxed">
              Programme les suivis post-achat, rappels avant expérience, demandes d’avis et suggestions de prochaine expérience sans toucher aux flux Stripe ou vouchers existants.
            </p>
          </div>
          <button
            onClick={loadJobs}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-slate-950 px-5 py-3 font-black text-xs uppercase tracking-widest hover:bg-cyan-50 transition disabled:opacity-60"
          >
            {loading ? <FaSpinner className="animate-spin" /> : <FaRedo />} Actualiser
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {[
          { label: 'En attente', value: stats.pending, icon: <FaClock />, cls: 'bg-amber-50 text-amber-700 border-amber-100' },
          { label: 'Envoyées', value: stats.sent, icon: <FaCheckCircle />, cls: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
          { label: 'Échecs', value: stats.failed, icon: <FaExclamationTriangle />, cls: 'bg-red-50 text-red-700 border-red-100' },
          { label: 'Total', value: stats.total, icon: <FaEnvelopeOpenText />, cls: 'bg-blue-50 text-blue-700 border-blue-100' },
        ].map((item) => (
          <div key={item.label} className={`rounded-3xl border p-5 ${item.cls}`}>
            <div className="flex items-center justify-between">
              <div className="text-2xl">{item.icon}</div>
              <div className="text-3xl font-black">{item.value}</div>
            </div>
            <div className="mt-3 text-[10px] font-black uppercase tracking-widest">{item.label}</div>
          </div>
        ))}
      </div>

      {message && (
        <div className={`rounded-2xl border p-4 text-sm font-bold ${message.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-red-50 border-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="grid xl:grid-cols-[420px_1fr] gap-6">
        <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-6 space-y-5 h-fit">
          <div>
            <h3 className="text-xl font-black text-slate-900">Créer une séquence</h3>
            <p className="text-sm text-slate-500 mt-1">Utilise ce module pour tester ou déclencher un suivi client.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Type de courriel</label>
              <select
                value={form.template_key}
                onChange={(e) => setForm((prev) => ({ ...prev, template_key: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
              >
                {TEMPLATE_OPTIONS.map((item) => (
                  <option key={item.key} value={item.key}>{item.label}</option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate-500">{selectedTemplate.description}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Courriel</label>
                <input
                  value={form.recipient_email}
                  onChange={(e) => setForm((prev) => ({ ...prev, recipient_email: e.target.value }))}
                  placeholder="client@email.com"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Nom</label>
                <input
                  value={form.recipient_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, recipient_name: e.target.value }))}
                  placeholder="Prénom"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Titre expérience</label>
              <input
                value={form.experience_title}
                onChange={(e) => setForm((prev) => ({ ...prev, experience_title: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Partenaire</label>
                <input
                  value={form.business_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, business_name: e.target.value }))}
                  placeholder="Nom partenaire"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Région</label>
                <input
                  value={form.region}
                  onChange={(e) => setForm((prev) => ({ ...prev, region: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Code voucher</label>
                <input
                  value={form.voucher_code}
                  onChange={(e) => setForm((prev) => ({ ...prev, voucher_code: e.target.value }))}
                  placeholder="FE-..."
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Planifier</label>
                <input
                  type="datetime-local"
                  value={form.scheduled_for}
                  onChange={(e) => setForm((prev) => ({ ...prev, scheduled_for: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Notes internes / contexte</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Message court, contexte ou consigne..."
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-medium outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <button
                onClick={handlePreview}
                disabled={!!actionLoading}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-widest text-slate-800 hover:bg-slate-50 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {actionLoading === 'preview' ? <FaSpinner className="animate-spin" /> : <FaEye />} Aperçu
              </button>
              <button
                onClick={() => handleCreate(false)}
                disabled={!!actionLoading}
                className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-xs font-black uppercase tracking-widest text-cyan-800 hover:bg-cyan-100 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {actionLoading === 'create' ? <FaSpinner className="animate-spin" /> : <FaCalendarAlt />} Planifier
              </button>
              <button
                onClick={() => handleCreate(true)}
                disabled={!!actionLoading}
                className="rounded-2xl bg-slate-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {actionLoading === 'send-now' ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />} Envoyer
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-5">
            <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setStatus(item.key)}
                    className={`rounded-2xl px-4 py-2 text-xs font-black uppercase tracking-widest transition ${status === item.key ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="relative w-full lg:w-80">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher email, voucher, partenaire..."
                  className="w-full rounded-2xl border border-slate-200 py-3 pl-11 pr-4 font-bold outline-none focus:ring-2 focus:ring-cyan-300"
                />
              </div>
            </div>
          </div>

          {previewHtml && (
            <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="font-black text-slate-900 flex items-center gap-2"><FaEye /> Aperçu courriel</div>
                <button onClick={() => setPreviewHtml(null)} className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200"><FaTimes /></button>
              </div>
              <iframe title="Aperçu courriel" src={previewHtml ? `data:text/html;charset=utf-8,${encodeURIComponent(previewHtml)}` : "about:blank"} sandbox="" className="w-full h-[520px] bg-slate-50" />
            </div>
          )}

          <div className="grid gap-4">
            {loading ? (
              <div className="rounded-[2rem] bg-white border border-slate-200 p-8 text-center text-slate-500 font-bold">
                <FaSpinner className="animate-spin mx-auto mb-3 text-2xl" /> Chargement des séquences...
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="rounded-[2rem] bg-white border border-slate-200 p-8 text-center">
                <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 text-2xl"><FaEnvelopeOpenText /></div>
                <div className="font-black text-slate-900">Aucune séquence trouvée</div>
                <p className="text-sm text-slate-500 mt-1">Crée un test ou change le filtre.</p>
              </div>
            ) : (
              filteredJobs.map((job) => (
                <div key={job.id} className="rounded-[2rem] bg-white border border-slate-200 shadow-sm p-5">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest ${statusBadge(job.status)}`}>
                          {statusIcon(job.status)} {job.status}
                        </span>
                        <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                          {job.template_key}
                        </span>
                      </div>
                      <div className="text-lg font-black text-slate-900 truncate">{job.subject || 'Séquence Forfeo'}</div>
                      <div className="text-sm text-slate-500 font-bold truncate">{job.recipient_name ? `${job.recipient_name} · ` : ''}{job.recipient_email}</div>
                      <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
                        <div className="rounded-2xl bg-slate-50 p-3"><span className="block text-slate-400 font-black uppercase tracking-widest text-[9px]">Planifiée</span><strong>{formatDate(job.scheduled_for)}</strong></div>
                        <div className="rounded-2xl bg-slate-50 p-3"><span className="block text-slate-400 font-black uppercase tracking-widest text-[9px]">Envoyée</span><strong>{formatDate(job.sent_at)}</strong></div>
                        <div className="rounded-2xl bg-slate-50 p-3"><span className="block text-slate-400 font-black uppercase tracking-widest text-[9px]">Tentatives</span><strong>{job.attempts || 0}</strong></div>
                      </div>
                      {job.last_error && <p className="mt-3 rounded-2xl bg-red-50 p-3 text-xs font-bold text-red-700">{job.last_error}</p>}
                    </div>

                    <div className="flex flex-wrap gap-2 xl:justify-end">
                      {job.status !== 'sent' && job.status !== 'cancelled' && (
                        <button
                          onClick={() => handleJobAction(job.id, 'send_now')}
                          disabled={!!actionLoading}
                          className="rounded-2xl bg-slate-950 text-white px-4 py-3 text-xs font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
                        >
                          {actionLoading === `send_now:${job.id}` ? <FaSpinner className="animate-spin" /> : <FaBolt />} Envoyer
                        </button>
                      )}
                      {job.status === 'failed' && (
                        <button
                          onClick={() => handleJobAction(job.id, 'retry')}
                          disabled={!!actionLoading}
                          className="rounded-2xl bg-blue-50 text-blue-700 px-4 py-3 text-xs font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
                        >
                          {actionLoading === `retry:${job.id}` ? <FaSpinner className="animate-spin" /> : <FaRedo />} Réessayer
                        </button>
                      )}
                      {job.status === 'pending' && (
                        <button
                          onClick={() => handleJobAction(job.id, 'cancel')}
                          disabled={!!actionLoading}
                          className="rounded-2xl bg-red-50 text-red-700 px-4 py-3 text-xs font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
                        >
                          {actionLoading === `cancel:${job.id}` ? <FaSpinner className="animate-spin" /> : <FaTrash />} Annuler
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
